/**
 * Flota y equipos (bloque 10 del plan de entrega, 2026-10-09): ficha de vehículos y equipos de la empresa (carro,
 * camión, montacargas, planta…) con su historial de mantenimiento y los viajes (traslados) en que se usaron.
 */
import { z } from 'zod';
import { decimalNoNegativo, paginacion, uuid } from './comun.ts';

export const TIPOS_EQUIPO = ['carro', 'camion', 'moto', 'montacargas', 'planta', 'otro'] as const;
export type TipoEquipo = (typeof TIPOS_EQUIPO)[number];
/** Los que pueden llevar mercancía en un traslado. */
export const TIPOS_VEHICULO: readonly TipoEquipo[] = ['carro', 'camion', 'moto'];
export const TIPOS_MANTENIMIENTO = ['aceite', 'cauchos', 'reparacion', 'repuesto', 'revision', 'otro'] as const;
export type TipoMantenimiento = (typeof TIPOS_MANTENIMIENTO)[number];

const texto = (max: number) => z.string().trim().max(max).nullable().optional();
/** Kilómetros u horas de uso: hasta 9.999.999.999, lo que cabe con holgura en la columna. */
const medida = decimalNoNegativo.refine((v) => Number(v) < 10_000_000_000, 'Ese valor es demasiado grande');

export const equipoInput = z.object({
  tipo: z.enum(TIPOS_EQUIPO),
  nombre: z.string().trim().min(2, 'Ponle un nombre que lo identifique').max(80),
  /** Placa (vehículos). Única entre los equipos activos. */
  placa: texto(15),
  marca: texto(60),
  modelo: texto(60),
  anio: z.number().int().min(1950).max(2100).nullable().optional(),
  serial: texto(60),
  /** Tienda a la que está asignado; null = de la empresa (se usa en cualquiera). */
  sucursal_id: uuid.nullable().optional(),
  notas: texto(500),
  activo: z.boolean().default(true),
});
export type EquipoInput = z.infer<typeof equipoInput>;
/** Edición (reemplazo completo): `activo` es obligatorio; un cuerpo sin él no reactiva un equipo dado de baja. */
export const equipoEditarInput = equipoInput.extend({ activo: z.boolean() });

export const mantenimientoInput = z.object({
  fecha: z.iso.date(),
  tipo: z.enum(TIPOS_MANTENIMIENTO),
  /** Qué se le hizo o qué pieza se cambió. */
  descripcion: z.string().trim().min(3, 'Describe el trabajo').max(300),
  costo_usd: decimalNoNegativo.default('0'),
  /** Kilometraje del vehículo al hacer el trabajo. */
  kilometraje: medida.nullable().optional(),
  /** Horas de uso (planta, montacargas). */
  horas: medida.nullable().optional(),
  /** Taller o persona que lo hizo. */
  taller: texto(120),
});
export type MantenimientoInput = z.infer<typeof mantenimientoInput>;

export const anularMantenimientoInput = z.object({ motivo: z.string().trim().min(3, 'Indica el motivo').max(300) });

export const equiposFiltros = paginacion.extend({
  q: z.string().trim().max(80).optional(),
  tipo: z.enum(TIPOS_EQUIPO).optional(),
  activo: z.enum(['true', 'false']).optional(),
});
export type EquiposFiltros = z.infer<typeof equiposFiltros>;

/** Vehículo para elegir en un traslado: sin costos ni historial. */
export interface VehiculoDTO {
  id: string;
  tipo: TipoEquipo;
  nombre: string;
  placa: string | null;
}

export interface EquipoDTO extends VehiculoDTO {
  marca: string | null;
  modelo: string | null;
  anio: number | null;
  serial: string | null;
  sucursal_id: string | null;
  sucursal_nombre: string | null;
  notas: string | null;
  activo: boolean;
  /** Mantenimientos vigentes (sin los anulados) y lo gastado en ellos. */
  mantenimientos: number;
  costo_total_usd: string;
  ultimo_mantenimiento: { fecha: string; tipo: TipoMantenimiento; descripcion: string } | null;
  ultimo_kilometraje: string | null;
  /** Traslados en los que se usó. */
  viajes: number;
}

export interface MantenimientoDTO {
  id: string;
  equipo_id: string;
  fecha: string;
  tipo: TipoMantenimiento;
  descripcion: string;
  costo_usd: string;
  kilometraje: string | null;
  horas: string | null;
  taller: string | null;
  registrado_por_nombre: string;
  creado_en: string;
  anulado: boolean;
  motivo_anulacion: string | null;
}

export interface ViajeEquipoDTO {
  traslado_id: string;
  numero_formateado: string;
  fecha: string;
  origen: string;
  destino: string;
  chofer_nombre: string | null;
  estado: string;
  unidades: string;
}

export interface EquipoDetalleDTO extends EquipoDTO {
  historial: MantenimientoDTO[];
  viajes_lista: ViajeEquipoDTO[];
}
